import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import * as RE from '~/reducers/experiences';

import SaveIcon from '@mui/icons-material/Save';
import { Alert, Box, Button, Container, SelectChangeEvent, debounce } from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { SearchModeOptions } from '~/components/Common/Forms/OfferExperiencesForm/ExperiencesFormFilters';
import OfferExperiencesForm from '~/components/Common/Forms/OfferExperiencesForm/OfferExperiencesForm';
import Spinner from '~/components/Common/Spinner';
import { FieldValueParams } from '~/components/Experiences/Home/Experiences';
import { useDismissible } from '~/components/Experiences/hooks';

import { FILTER_RANGE_KEYS, FilterKeys } from '~/consts/experiences';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  Filters,
  getCategories,
  getExperienceOffers,
  getHandpickedExperiencesForOffer,
  postHandpickedExperiencesForOffer,
} from '~/services/ExperiencesService';
import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

type DismissibleAlert = {
  type: 'success' | 'error';
  message: string;
};

interface Props {
  offer: App.Offer;
  match: any;
}

const DEFAULT_RADIUS_TO_SEARCH_EXPERIENCES = 50;
const DEFAULT_MAX_EXPERIENCES_TO_SHOW = 150;
const SIZE_PER_PAGE = 10;

function ExperiencesEditContainer({ match }: Props) {
  const offerId = match.params.id_offer;
  const coordsRef = useRef<{ lat: number; lng: number } | null>(null);
  const { tenant } = useCurrentTenant();

  const [isFetching, setIsFetching] = useState(true);

  const [experiences, setExperiences] = useState<Array<Experiences.Offer>>([]);
  const [page, setPage] = useState(1);
  const [experiencesTotal, setExperiencesTotal] = useState(0);

  const [selectedExperiences, setSelectedExperiences] = useState<Array<Experiences.Offer>>([]);
  const [expInFlow, setExperiencesInFlow] = useState(null);
  const [expCurated, setExperiencesCurated] = useState(null);
  const [showOnlyExperiencesCurated, setShowOnlyExperiencesCurated] = useState(null);
  const [fieldSearchValue, setFieldSearchValue] = useState('');
  const [filters, setFilters] = useState<Filters>({});
  const [searchModeSelected, setSearchModeSelected] = useState(SearchModeOptions.TITLE);
  const [alert, setAlert] = useDismissible<DismissibleAlert>({
    autoDismissAfterMs: 5000,
  });

  const [categories, categoriesDispatch] = useReducer(RE.fetchCategoriesReducer, RE.initialCategories);

  const handleSearchFieldValue = useCallback((fieldValue: string) => {
    setFieldSearchValue(fieldValue);
  }, []);

  const setFilter = useCallback((name: keyof Filters, value: unknown) => {
    setFilters((filters) => ({ ...filters, [name]: value }));
  }, []);

  const setFiltersByKey = useCallback(
    (key: FilterKeys, value: string) => {
      const items = filters[key] ?? [];
      setFilter(key, items.includes(value) ? items.filter((item) => item !== value) : [...items, value]);
    },
    [filters.categories, filters.status, filters.providerIn],
  );

  const setGoLiveDateFilter = useCallback(
    (value: Filters['goLiveDateInWeeks']) => {
      setFilter('goLiveDateInWeeks', value);
    },
    [setFilter],
  );

  const handleSearchModeSelected = useCallback((event: SelectChangeEvent) => {
    setSearchModeSelected(event.target.value as SearchModeOptions);
  }, []);

  const handlePage = useCallback((_, page: number) => setPage(page), []);

  const handleFieldValue = useCallback(
    (params: FieldValueParams) => {
      const { key, range, fieldValue } = params;

      if (FILTER_RANGE_KEYS.includes(key)) {
        setFilters((filters) => ({
          ...filters,
          [key]: { ...filters[key], [range]: fieldValue },
        }));
      }
    },
    [filters.price, filters.rating],
  );

  const refetchExperiences = useCallback(() => {
    deboucedFetchExperiences(1, filters);
  }, [filters]);

  const onChangeSearchValue = (fieldSearchValue: string, searchModeSelected: string) => {
    Object.values(SearchModeOptions).forEach((key) => delete filters[key]);
    filters[searchModeSelected] = fieldSearchValue;
    refetchExperiences();
    setFilters(filters);
  };

  const fetchCategories = async (): Promise<void> => {
    categoriesDispatch({ type: 'LOAD' });
    try {
      const res = await getCategories(tenant.brand);

      if (res?.errors?.length > 0) {
        categoriesDispatch({
          type: 'ERROR',
          message: '[ERROR] No categories response exists.',
        });
      }

      categoriesDispatch({
        type: 'FETCH',
        payload: res.result,
      });
    } catch (error) {
      console.error('ERROR TO FETCH CATEGORIES: ', error);
      categoriesDispatch({ type: 'ERROR', message: error.message });
    }
  };

  const fetchPagedExperiences = useCallback(async (pageNumber: number, filters: Filters = {}) => {
    if (!coordsRef.current) {
      return;
    }

    setIsFetching(true);
    try {
      const offset: number = (pageNumber - 1) * SIZE_PER_PAGE;
      const filtersToSend = !filters?.idIn?.length
        ? {
            distance: `${DEFAULT_RADIUS_TO_SEARCH_EXPERIENCES}km`,
            status: ['APPROVED'],
            coordinates: coordsRef.current,
            ...filters,
          }
        : filters;

      const experiences = await getExperienceOffers({
        curationData: true,
        limit: DEFAULT_MAX_EXPERIENCES_TO_SHOW,
        offset,
        filters: filtersToSend,
        brand: 'luxuryescapes',
        sort: 'SCORE',
        sortOrientation: 'DESC',
      });

      setExperiences(experiences.result);
      setExperiencesTotal(experiences.total);
    } catch (error) {
      console.error('Error fetching paged experiences:', error);
    } finally {
      setIsFetching(false);
    }
  }, []);

  const deboucedFetchExperiences = useMemo(() => debounce(fetchPagedExperiences, 1000), [fetchPagedExperiences]);

  const fetchExperiencesWithFilters = useCallback(() => {
    fetchPagedExperiences(1, filters);
  }, [filters]);

  const fetchInitialData = useCallback(async () => {
    setIsFetching(true);
    try {
      const [offerResponse, experiencesResponse] = await Promise.all([
        OffersService.getOffer(offerId),
        getHandpickedExperiencesForOffer(offerId),
      ]);

      if (experiencesResponse.result.length > 0) {
        const storedSelectedExperiencesIds = experiencesResponse.result.map((i: any) => i.experienceId).join(',');
        const storedSelectedExperiences = await getExperienceOffers({
          curationData: true,
          limit: storedSelectedExperiencesIds.length,
          filters: {
            idIn: storedSelectedExperiencesIds,
          },
          brand: 'luxuryescapes',
        });
        setSelectedExperiences(storedSelectedExperiences.result);
      }

      const property = await ReservationService.getProperty(offerResponse.result.packages[0].fk_property_id);

      coordsRef.current = {
        lat: property.result.latitude,
        lng: property.result.longitude,
      };

      setExperiencesInFlow(offerResponse.result.experiences_in_flow);
      setExperiencesCurated(offerResponse.result.experiences_curated);
      setShowOnlyExperiencesCurated(offerResponse.result.show_only_experiences_curated);

      await fetchPagedExperiences(1);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setIsFetching(false);
    }
  }, [fetchPagedExperiences, offerId]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (coordsRef.current && page > 0) {
      fetchPagedExperiences(page);
    }
  }, [page, fetchPagedExperiences]);

  useEffect(() => {
    onChangeSearchValue(fieldSearchValue, searchModeSelected);
  }, [fieldSearchValue]);

  const onSubmitForm = async () => {
    setIsFetching(true);
    try {
      await Promise.all([
        // Patch offer
        OffersService.updateOffer(
          {
            experiences_in_flow: expInFlow,
            experiences_curated: expCurated,
            show_only_experiences_curated: showOnlyExperiencesCurated,
          },
          offerId,
          { acceptLanguage: 'en' },
        ),
        // Patch experiences
        postHandpickedExperiencesForOffer({
          offerId,
          override: true,
          payload: selectedExperiences.map((experienceId) => {
            return {
              offerSalesforceId: offerId,
              experienceId: experienceId.id,
            };
          }),
        }),
        setAlert({ type: 'success', message: 'Updated experiences with success!' }),
      ]);
    } catch (error) {
      console.error('Error submitting form:', error);
      setAlert({ type: 'error', message: 'Error submitting form!' });
    } finally {
      setIsFetching(false);
    }
  };

  const resetFilters = () => {
    setFilters({});
  };

  if (isFetching && experiences.length === 0) {
    return <Spinner />;
  }

  return (
    <Container>
      <OfferBreadcrumbs offerId={offerId} currentPage="Experiences" />
      <PageSubheader title="Hotel Experiences Offers Curation">
        <Button variant="contained" color="primary" onClick={onSubmitForm} startIcon={<SaveIcon fontSize="small" />}>
          Save
        </Button>
      </PageSubheader>
      <Box>
        {alert && (
          <Alert severity={alert.type} style={{ textAlign: 'center' }}>
            <strong>{alert.message}</strong>
          </Alert>
        )}
      </Box>

      <OfferExperiencesForm
        expInFlowChecked={expInFlow}
        setExperiencesInFlow={setExperiencesInFlow}
        expCuratedChecked={expCurated}
        setExperiencesCurated={setExperiencesCurated}
        experiences={experiences}
        selectedExperiences={selectedExperiences}
        setSelectedExperiences={setSelectedExperiences}
        showOnlyExperiencesCurated={showOnlyExperiencesCurated}
        setShowOnlyExperiencesCurated={setShowOnlyExperiencesCurated}
        sizePerPage={SIZE_PER_PAGE}
        experiencesTotal={experiencesTotal}
        handlePage={handlePage}
        page={page}
        isFetching={isFetching}
        filters={filters}
        categories={categories}
        resetFilters={resetFilters}
        setFiltersByKey={setFiltersByKey}
        fieldSearchValue={fieldSearchValue}
        handleFieldValue={handleFieldValue}
        handleSearchFieldValue={handleSearchFieldValue}
        fetchExperiencesWithFilters={fetchExperiencesWithFilters}
        showGoLiveDateFilter
        setGoLiveDateFilter={setGoLiveDateFilter}
        searchModeSelected={searchModeSelected}
        setSearchModeSelected={handleSearchModeSelected}
      />
    </Container>
  );
}

export default ExperiencesEditContainer;
